import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { InferGetStaticPropsType } from 'next';

import { IconButton, XCloseIcon } from '@blockworks/ui';
import { cn } from '@blockworks/ui/utils';
import { VisualizationWorkerProvider } from '@blockworks/viz';

import VisualizationPageLayout from '@/components/pages/analytics/components/visualization-page-layout';
import { isVisualizationPageProps } from '@/components/pages/analytics/typeguards/is-visualization-page-props';
import NewsRecirculationSection from '@/components/sections/content/news-recirculation-section';

import AnalyticsPageStaticProps from './analytics-page-static-props';
import DashboardPageLayout from './components/dashboard-page-layout';
import EtfPageLayout from './components/etf-page-layout';
import Menu from './components/menu';
import MobileMenu from './components/mobile-menu';
import { isDashboardPageProps } from './typeguards/is-dashboard-page-props';
import { isEtfPageProps } from './typeguards/is-etf-page-props';
import filterMenuData from './utils/filter-menu-data';

const AnalyticsPage = ({
    content,
    menuData,
    selectedDataPageSlug,
}: InferGetStaticPropsType<typeof AnalyticsPageStaticProps>) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredMenuData, setFilteredMenuData] = useState(menuData);
    const [isSearchActive, setIsSearchActive] = useState(false);

    const mobileMenuData =
        filteredMenuData?.flatMap?.(curr =>
            curr.options.map(({ name, href, slug }) => ({
                name,
                href,
                slug,
            })),
        ) ?? [];

    const selectedMobileOption = mobileMenuData.find(item => item.slug === selectedDataPageSlug);

    const handleFilterMenuData = (searchTerm: string) => {
        setSearchTerm(searchTerm);
        setFilteredMenuData(filterMenuData(menuData, searchTerm));
    };

    const searchInputRef = useRef<HTMLInputElement | null>(null);

    /**
     * Focuses the search input when `isSearchActive` is true.
     * `useEffect` ensures the input exists in the DOM before focusing,
     * avoiding race conditions from async state updates.
     */
    useEffect(() => {
        if (isSearchActive) {
            searchInputRef.current?.focus();
        }
    }, [isSearchActive]);

    return (
        <VisualizationWorkerProvider>
            <main className="flex flex-col w-full">
                <div className={cn('lg:hidden px-10 pt-5', { hidden: isVisualizationPageProps(content) })}>
                    <MobileMenu
                        setSearchTerm={handleFilterMenuData}
                        searchOptions={mobileMenuData}
                        searchTerm={searchTerm}
                        selectedOption={selectedMobileOption}
                        setIsSearchActive={setIsSearchActive}
                    />
                </div>

                <div className="flex">
                    <section className="hidden lg:block overflow-auto bg-gray-75 w-[185px]">
                        {isSearchActive && (
                            <div className="flex justify-center items-center bg-white flex-grow w-full px-4 py-1.5 border-b">
                                <input
                                    ref={searchInputRef}
                                    id="searchInput"
                                    type="text"
                                    onChange={ev => {
                                        const value = ev.target.value;
                                        handleFilterMenuData(value);
                                    }}
                                    value={searchTerm}
                                    placeholder="Search..."
                                    aria-label="Search analytics menu"
                                    role="searchbox"
                                    className="w-full min-w-0 appearance-none rounded-none border bg-white py-0.5 px-1.5 text-xs text-left text-gray-900 placeholder-gray-400 focus:border-transparent border-transparent focus:outline-none focus:ring-1 focus:ring-transparent"
                                />
                                <IconButton
                                    size="sm"
                                    aria-label="Close Search"
                                    icon={XCloseIcon}
                                    variant="ghost"
                                    type="reset"
                                    borderRadius="full"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilteredMenuData(menuData);
                                        setIsSearchActive(false);
                                    }}
                                    classNames={{
                                        base: 'ml-1 hover:bg-transparent active:bg-transparent',
                                    }}
                                />
                            </div>
                        )}

                        <Menu
                            isSearchActive={isSearchActive}
                            data={filteredMenuData}
                            setIsSearchActive={setIsSearchActive}
                            selectedSlugOption={selectedDataPageSlug}
                        />
                    </section>
                    <div className="flex-1 border-l">
                        {isEtfPageProps(content) && (
                            <EtfPageLayout
                                asset={content.asset}
                                etfs={content.etfs}
                                lastUpdated={content.lastUpdated}
                                explanationPageHtml={content.explanationPageContent}
                                sponsoredEtfs={content.sponsoredEtfs}
                            />
                        )}
                        {isDashboardPageProps(content) && <DashboardPageLayout {...content} />}
                        {isVisualizationPageProps(content) && <VisualizationPageLayout {...content} />}
                    </div>
                </div>

                <section>
                    {isDashboardPageProps(content) && !content.datapageDashboardDescriptionContent && (
                        <NewsRecirculationSection
                            algoliaSearchKeyword={content.datapageName}
                            title="Related News"
                            count={4}
                            paddingClassName="p-6"
                            className="p-0"
                            imageQuality={10}
                            linkTitle={
                                <span className="flex">
                                    more from news
                                    <svg
                                        width={8}
                                        height={4}
                                        viewBox="0 0 8 4"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="ml-1 mt-1.5"
                                        preserveAspectRatio="none"
                                    >
                                        <path d="M6.005 1.5H0V2.5H6.005V4L8 2L6.005 0V1.5Z" fill="#5637CD" />
                                    </svg>
                                </span>
                            }
                            linkUrl="/news"
                        />
                    )}
                </section>
            </main>
        </VisualizationWorkerProvider>
    );
};

export default AnalyticsPage;
